<template>
  <div
    class="preg"
    :class="[tone === 'admin' ? 'preg--admin' : 'preg--seller', { 'preg--sale': mallKind === 'SALE' }]"
  >
    <div class="preg__main">
      <header class="preg__intro">
        <h2 class="preg__title">{{ isNew ? '상품 등록' : '상품 수정' }}</h2>
        <p class="preg__lead">새로운 상품을 등록하고, 쇼핑몰에 노출할 수 있습니다.</p>
      </header>

      <section class="preg__card">
        <h3 class="preg__card-title">상품 기본 정보</h3>

        <div class="preg__field">
          <span class="preg__label">판매 구분 <em>*</em></span>
          <div class="preg__kinds" role="radiogroup" aria-label="판매 구분">
            <label class="preg__kind" :class="{ 'is-on': mallKind === 'REGULAR' }">
              <input v-model="mallKind" type="radio" value="REGULAR" />
              <strong>상시몰</strong>
              <span>항상 판매</span>
            </label>
            <label class="preg__kind preg__kind--sale is-locked" :class="{ 'is-on': mallKind === 'SALE' }">
              <input v-model="mallKind" type="radio" value="SALE" disabled />
              <strong>공동구매몰</strong>
              <span>잠시 등록 중단</span>
            </label>
          </div>
          <p class="preg__hint">지금은 공동구매몰에 상품을 올릴 수 없습니다. 상시몰만 등록됩니다.</p>
          <p v-if="mallKind === 'REGULAR'" class="preg__hint">
            1개 판매가는 묶음 금액을 계산하는 기준입니다. 쇼핑몰에는 아래에서 고른 구성의 총액이 먼저 보입니다.
          </p>
          <p v-else class="preg__hint">이미 공동구매인 상품만 그 구분을 유지합니다. 새로 공동구매로 바꾸지는 못합니다.</p>
        </div>

        <label class="preg__field">
          <span class="preg__label">상품명 <em>*</em></span>
          <input
            v-model="form.name"
            class="preg__input"
            maxlength="150"
            placeholder="예: [본사정품] 올인원 멀티비타민 이뮨샷 7개입, 8박스"
          />
          <span class="preg__hint">고객이 보는 제목입니다. 개수·박스·회분은 상품명에 직접 적으세요.</span>
        </label>

        <label class="preg__field">
          <span class="preg__label">카테고리 <em>*</em></span>
          <select v-model="form.category" class="preg__input">
            <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
          </select>
        </label>

        <div class="preg__field" data-tour="seller-product-origin">
          <span class="preg__label">원산지 <em v-if="originMust">*</em></span>
          <div class="preg__radios" role="radiogroup" aria-label="원산지">
            <label v-for="item in originKinds" :key="item.id || 'none'" class="preg__radio">
              <input v-model="originKind" type="radio" :value="item.id" />
              {{ item.label }}
            </label>
          </div>
          <label v-if="originKind === 'IMPORT'" class="preg__origin-detail">
            <span class="preg__label">국가명 <em>*</em></span>
            <input
              v-model="originDetail"
              class="preg__input"
              maxlength="60"
              placeholder="예: 미국"
              aria-label="수입 국가명"
            />
          </label>
          <label v-if="originKind === 'MIXED'" class="preg__origin-detail">
            <span class="preg__label">국가별 원산지 <em>*</em></span>
            <textarea
              v-model="originDetail"
              class="preg__input preg__textarea"
              rows="2"
              maxlength="160"
              placeholder="예: 쌀 국내산, 밀 미국산"
              aria-label="국가별 원산지"
            />
          </label>
          <label v-if="originKind === 'OTHER'" class="preg__origin-detail">
            <span class="preg__label">원산지 내용 <em>*</em></span>
            <input
              v-model="originDetail"
              class="preg__input"
              maxlength="80"
              placeholder="표시할 원산지를 적으세요"
              aria-label="기타 원산지"
            />
          </label>
          <p class="preg__hint">{{ originHint }}</p>
        </div>

        <div v-if="canEditStatus" class="preg__field">
          <span class="preg__label">상품 상태 <em>*</em></span>
          <div ref="statusRadioRoot" class="preg__radios" role="radiogroup" aria-label="상품 상태">
            <label v-for="item in saleStatusChoices" :key="item.value" class="preg__radio">
              <input
                type="radio"
                :name="statusRadioName"
                :value="item.value"
                :checked="saleStatus === item.value"
                @click="pickSaleStatus(item.value, $event)"
                @keydown.space="pickSaleStatus(item.value, $event)"
              />
              {{ item.label }}
            </label>
          </div>
        </div>

        <label class="preg__field">
          <span class="preg__label">상품 설명</span>
          <textarea
            v-model="form.description"
            class="preg__input preg__textarea"
            maxlength="4000"
            rows="5"
            placeholder="상품을 짧게 소개하세요."
          />
        </label>

        <div class="preg__field">
          <span class="preg__label">상품 이미지</span>
          <div class="preg__images">
          <div v-for="(url, index) in form.images" :key="url + index" class="preg__image">
            <img :src="url" alt="" />
            <span v-if="index === 0" class="preg__image-badge">대표 이미지</span>
            <button type="button" class="preg__image-del" aria-label="이미지 삭제" @click="removeImage(index)">
              ×
            </button>
          </div>
          <button
            v-if="form.images.length < maxImages"
            type="button"
            class="preg__image preg__image--add"
            :disabled="uploading"
            @click="pickImages"
          >
            <span>+</span>
            이미지 추가
            <small>(최대 {{ maxImages }}장)</small>
          </button>
        </div>
        <p class="preg__note">권장 이미지 사이즈 800 × 800px 이상 · 파일 형식 JPG, PNG (최대 10MB)</p>
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          hidden
          @change="onFiles"
        />
        </div>

        <div class="preg__field">
          <span class="preg__label">재고 수량 <em>*</em></span>
          <template v-if="stockLocked">
            <p class="preg__locked">재고 {{ form.stock.toLocaleString('ko-KR') }}{{ sellNoun }}</p>
            <button type="button" class="preg__text-btn" @click="emit('open-stock-history')">재고·이력에서 추가</button>
          </template>
          <span v-else class="preg__unit">
            <input v-model.number="form.stock" class="preg__input" type="number" min="0" />
            <em>{{ sellNoun }}</em>
          </span>
        </div>
      </section>

      <section class="preg__card" data-tour="seller-product-detail">
        <div class="preg__card-head">
          <h3 class="preg__card-title">상세 정보</h3>
          <span class="preg__hint">{{ form.blocks.length }}개</span>
        </div>
        <p class="preg__hint">올린 이미지가 상품 화면 본문에 위에서부터 순서대로 보입니다.</p>
        <button
          type="button"
          class="preg__drop"
          :class="{ 'is-over': detailDragOver }"
          :disabled="uploading"
          @click="addDetailImages"
          @dragover.prevent="detailDragOver = true"
          @dragleave="detailDragOver = false"
          @drop.prevent="onDetailDrop"
        >
          <AppIcon :icon="uploading ? 'mdi-progress-upload' : 'mdi-tray-arrow-up'" :size="28" />
          <span>{{ uploading ? '이미지를 올리는 중입니다' : '본문 이미지 올리기' }}</span>
          <small>클릭하거나 이미지를 끌어다 놓으세요. 여러 장 가능</small>
        </button>
        <p v-if="!form.blocks.length" class="preg__note">이미지가 본문입니다. 글이 필요하면 소제목·문단·안내를 아래에 더합니다.</p>
        <ol v-else class="preg__blocks">
          <li v-for="(block, index) in form.blocks" :key="block.key" class="preg__block">
            <div class="preg__block-head">
              <strong>{{ blockLabel(block.type) }}</strong>
              <div class="preg__block-tools">
                <button type="button" aria-label="위로" :disabled="index === 0" @click="moveBlock(index, -1)">
                  <AppIcon icon="mdi-arrow-up" :size="16" />
                </button>
                <button
                  type="button"
                  aria-label="아래로"
                  :disabled="index === form.blocks.length - 1"
                  @click="moveBlock(index, 1)"
                >
                  <AppIcon icon="mdi-arrow-down" :size="16" />
                </button>
                <button type="button" aria-label="블록 삭제" @click="removeBlock(index)">
                  <AppIcon icon="mdi-trash-can-outline" :size="16" />
                </button>
              </div>
            </div>
            <input
              v-if="block.type === 'HEADING'"
              v-model="block.text"
              class="preg__input"
              maxlength="80"
              placeholder="소제목을 입력하세요"
            />
            <textarea
              v-else-if="block.type === 'TEXT' || block.type === 'NOTICE'"
              v-model="block.text"
              class="preg__input preg__textarea"
              :rows="block.type === 'NOTICE' ? 2 : 4"
              maxlength="2000"
              :placeholder="block.type === 'NOTICE' ? '안내 문구를 입력하세요' : '본문 문단을 입력하세요'"
            />
            <div v-else class="preg__block-image">
              <button
                v-if="!block.imageUrl"
                type="button"
                class="preg__drop preg__drop--slim"
                :disabled="uploading"
                @click="pickBlockImage(block.key)"
              >
                <AppIcon icon="mdi-tray-arrow-up" :size="22" />
                <span>이미지 올리기</span>
              </button>
              <div v-else class="preg__block-thumb">
                <img :src="block.imageUrl" alt="" />
              </div>
              <div class="preg__block-image-side">
                <button
                  v-if="block.imageUrl"
                  type="button"
                  class="preg__upload-btn"
                  :disabled="uploading"
                  @click="pickBlockImage(block.key)"
                >
                  <AppIcon icon="mdi-tray-arrow-up" :size="16" />
                  이미지 교체
                </button>
                <input v-model="block.alt" class="preg__input" maxlength="150" placeholder="이미지 설명을 입력하세요" />
              </div>
            </div>
          </li>
        </ol>
        <div class="preg__block-add">
          <button type="button" @click="addBlock('HEADING')">소제목</button>
          <button type="button" @click="addBlock('TEXT')">문단</button>
          <button type="button" @click="addDetailImages">이미지 올리기</button>
          <button type="button" @click="addBlock('NOTICE')">안내</button>
        </div>
      </section>

      <section class="preg__card" data-tour="product-editor-shipping">
        <div class="preg__card-head">
          <h3 class="preg__card-title">배송 정보</h3>
          <p class="preg__ship-free" title="고객에게 배송비를 따로 받지 않습니다. 판매가격에 포함합니다.">* 무료배송</p>
        </div>
        <div class="preg__stack">
          <div class="preg__field">
            <span class="preg__label">출고 소요 <em>*</em></span>
            <div class="preg__lead-picks">
              <button
                v-for="lead in leadTimes"
                :key="lead"
                type="button"
                :class="{ 'is-on': form.leadTime === lead }"
                @click="form.leadTime = lead"
              >
                {{ lead }}
              </button>
            </div>
            <input
              v-model="form.leadTime"
              class="preg__input"
              type="text"
              maxlength="30"
              placeholder="직접 입력"
              aria-label="출고 소요"
            />
          </div>
        </div>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">가격 정보</h3>
        <div class="preg__stack">
          <div class="preg__field">
            <div class="preg__ship-line">
              <span class="preg__ship-chunk">
                <input
                  v-model.number="form.allocShipMax"
                  class="preg__input"
                  type="number"
                  min="1"
                  aria-label="배송비 기준 수량"
                />
                <span>개당 배송비</span>
              </span>
              <span class="preg__ship-chunk preg__ship-chunk--fee">
                <WonInput v-model="form.allocShipFee" aria-label="배송비" />
                <span>원</span>
              </span>
            </div>
            <span class="preg__hint">고객에게는 무료배송입니다. 이 금액은 아래 판매가격에 넣고, 환불할 때도 같습니다.</span>
          </div>
          <div class="preg__price-pair" :class="{ 'is-off': !shipLineReady }">
            <label class="preg__field">
              <span class="preg__label">{{ goodsPriceLabel }}공급가</span>
              <span class="preg__unit">
                <WonInput
                  v-model="form.basisCost"
                  :disabled="!shipLineReady"
                  :class="{ 'is-zero': shipLineReady && !Number(form.basisCost) }"
                />
                <em>원</em>
              </span>
            </label>
            <label class="preg__field">
              <span class="preg__label">{{ goodsPriceLabel }}판매가격</span>
              <span class="preg__unit">
                <WonInput
                  v-model="form.basisPrice"
                  :disabled="!shipLineReady"
                  :class="{ 'is-zero': shipLineReady && !Number(form.basisPrice) }"
                />
                <em>원</em>
              </span>
            </label>
          </div>
          <p v-if="!shipLineReady" class="preg__hint">개당 배송비 수량과 금액을 적으면 공급가와 판매가격을 쓸 수 있습니다.</p>
        </div>

        <div data-tour="product-editor-sell">
        <template v-if="mallKind === 'SALE'">
          <label class="preg__field">
            <span class="preg__label">1카톤 개수 <em>*</em></span>
            <span class="preg__unit">
              <input v-model.number="form.packQty" class="preg__input" type="number" min="1" :disabled="!optionsReady" />
              <em>개</em>
            </span>
            <span class="preg__hint">
              한 카톤에 들어 있는 개수입니다. 목록에는 1개 가격이 먼저 보이고, 상품을 열면 카톤 가격과 이 개수 묶음이 보입니다. 최소 주문은 1카톤입니다.
            </span>
          </label>
          <p v-if="sellingPrice > 0" class="preg__hint">
            1개당 {{ priceText }} · 1카톤 {{ cartonPriceText }} · {{ cartonPieces }}개 묶음
          </p>
        </template>

        <template v-else>
          <div class="preg__opts" :class="{ 'is-off': !optionsReady }">
            <div class="preg__opts-head">
              <strong>구매 옵션</strong>
              <span>동그라미로 고른 가격이 상품 목록에 보이고, 상품 화면에서는 그 옵션이 미리 선택됩니다.</span>
            </div>
            <div class="preg__qty-add">
              <button
                type="button"
                class="preg__qty-make"
                :disabled="!optionsReady"
                @click="fillSuggestedPrices"
              >
                자동옵션생성
              </button>
              <button type="button" :disabled="!optionsReady || !qtyRows.length" @click="recalculateOptions">
                자동계산
              </button>
              <label class="preg__qty-custom">
                <input
                  v-model.number="customQty"
                  type="number"
                  min="1"
                  aria-label="추가할 개수"
                  :disabled="!optionsReady"
                />
                <em>개</em>
                <button type="button" :disabled="!optionsReady" @click="addQty(customQty)">추가</button>
              </label>
            </div>
            <p v-if="!optionsReady" class="preg__hint">공급가와 판매가격을 모두 적으면 구매 옵션을 쓸 수 있습니다.</p>
            <p v-else-if="!qtyRows.length" class="preg__hint">아직 옵션이 없습니다. 개수를 적고 추가하거나 자동옵션생성을 누르세요.</p>
            <ul v-if="qtyRows.length" class="preg__opt-list">
              <li
                v-for="row in qtyRows"
                :key="row.opt.key"
                class="preg__qty-row"
                :class="{ 'is-front': row.opt.front }"
              >
                <label class="preg__front">
                  <input
                    type="radio"
                    name="preg-storefront"
                    :checked="row.opt.front === true"
                    :disabled="!optionsReady"
                    :aria-label="`${row.opt.name}을 상품 목록에 표시`"
                    @change="setFront(row.opt)"
                  />
                </label>
                <strong>{{ row.opt.name }}</strong>
                <label class="preg__qty-price">
                  <WonInput
                    :model-value="row.total"
                    :disabled="!optionsReady"
                    :aria-label="`${row.opt.name} 판매가격`"
                    @update:model-value="onBundlePrice(row.opt, $event)"
                  />
                  <em>원</em>
                  <span v-if="row.opt.front" class="preg__front-mark">목록에 표시</span>
                </label>
                <button
                  type="button"
                  class="preg__text-btn"
                  :disabled="!optionsReady"
                  :aria-label="`${row.opt.name} 삭제`"
                  @click="removeOption(row.opt)"
                >
                  삭제
                </button>
                <span v-if="row.breakdown" class="preg__opt-note">
                  <span>개당공급가 {{ money(row.breakdown.unitSupply) }}</span>
                  <span>배송비 {{ money(row.breakdown.unitShip) }}</span>
                  <span>개당판매가 {{ money(row.breakdown.unitSell) }}</span>
                  <span>개당마진 {{ money(row.breakdown.unitMargin) }}</span>
                </span>
                <span v-else-if="row.qty" class="preg__opt-note">
                  개당판매가 {{ money(Math.round(row.total / row.qty)) }}
                </span>
                <span v-else class="preg__opt-note">{{ row.note }}</span>
              </li>
            </ul>
          </div>
        </template>
        </div>
        <p class="preg__margin-note">마진배분율 : {{ DEFAULT_MARGIN_SHARE }}%</p>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">관리자 메모</h3>
        <label class="preg__field">
          <span class="preg__label">검수에 남길 말</span>
          <textarea
            v-model="form.requestNote"
            class="preg__input preg__textarea"
            rows="3"
            maxlength="500"
            placeholder="검수 때 참고할 말을 남기세요. 선택입니다."
          />
        </label>
      </section>
    </div>

    <aside class="preg__preview" data-tour="seller-product-preview-map">
      <div class="preg__preview-tabs" role="tablist" aria-label="상품 미리보기">
        <button
          type="button"
          role="tab"
          class="preg__preview-tab"
          :class="{ 'is-on': previewTab === 'card' }"
          :aria-selected="previewTab === 'card'"
          @click="previewTab = 'card'"
        >
          목록 카드
        </button>
        <button
          type="button"
          role="tab"
          class="preg__preview-tab"
          :class="{ 'is-on': previewTab === 'buy' }"
          :aria-selected="previewTab === 'buy'"
          @click="previewTab = 'buy'"
        >
          구매 화면
        </button>
      </div>
      <div v-show="previewTab === 'card'" class="preg__preview-pane" role="tabpanel">
        <ProductCard :product="previewProduct" mode="static" />
      </div>
      <div v-show="previewTab === 'buy'" class="preg__preview-pane" role="tabpanel">
        <MallProductBuyPanel :product="previewProduct" />
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import type { UploadedImage } from '@/api/mallProduct'
import AppIcon from '@/components/common/AppIcon.vue'
import WonInput from '@/components/manage/WonInput.vue'
import MallProductBuyPanel from '@/components/mall/MallProductBuyPanel.vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import type { MallDetailBlock } from '@/constants/mallProducts'
import {
  BLOCK_LABEL,
  LEAD_TIMES,
  MAX_PRODUCT_IMAGES,
  ORIGIN_KIND_OPTIONS,
  composeOrigin,
  applySaleCarton,
  DEFAULT_MARGIN_SHARE,
  nextRowKey,
  originRequiredFor,
  toPreviewProduct,
  type EditorOption,
  type OriginKind,
  type ProductEditorForm,
} from '@/constants/productEditor'
import { optionPieceCount, sellUnitNoun } from '@/utils/mallSell'
import { packQuoteReady, quotePack, suggestPackQtys, type PackQuote } from '@/utils/mallPackQuote'

type MallKind = 'REGULAR' | 'SALE'

const props = withDefaults(
  defineProps<{
    form: ProductEditorForm
    tone?: 'seller' | 'admin'
    storeName?: string
    categories: string[]
    uploader: (files: File[]) => Promise<UploadedImage[]>
    storeShipping: { returnFee: number; cutoff: string; returnPolicy: string }
    storeShippingReadonly?: boolean
    stockLocked?: boolean
    saleStatus?: string
    canEditStatus?: boolean
    isNew?: boolean
  }>(),
  {
    tone: 'seller',
    storeName: '',
    storeShippingReadonly: false,
    stockLocked: false,
    saleStatus: '',
    canEditStatus: false,
    isNew: true,
  },
)

const emit = defineEmits<{
  notify: [message: string]
  'open-stock-history': []
  'update-status': [status: 'SELLING' | 'SOLDOUT' | 'HIDDEN']
}>()

const saleStatusChoices = [
  { value: 'SELLING', label: '판매중' },
  { value: 'SOLDOUT', label: '품절' },
  { value: 'HIDDEN', label: '판매중지' },
] as const

const statusRadioName = `preg-sale-status-${Math.random().toString(36).slice(2, 8)}`
const statusRadioRoot = ref<HTMLElement | null>(null)

function syncSaleStatusRadios() {
  const root = statusRadioRoot.value
  if (!root) return
  root.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((input) => {
    input.checked = input.value === props.saleStatus
  })
}

function pickSaleStatus(status: 'SELLING' | 'SOLDOUT' | 'HIDDEN', event: Event) {
  event.preventDefault()
  syncSaleStatusRadios()
  if (props.saleStatus === status) return
  emit('update-status', status)
}

watch(
  () => props.saleStatus,
  async () => {
    await nextTick()
    syncSaleStatusRadios()
  },
)

const form = props.form
const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const blockImageKey = ref<number | 'new' | null>(null)
const detailDragOver = ref(false)
const maxImages = MAX_PRODUCT_IMAGES
const leadTimes = LEAD_TIMES
const mallKind = computed<MallKind>({
  get() {
    return form.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE'
  },
  set(value) {
    form.saleCode = ''
    /* 공동구매몰 신규 등록은 잠시 막는다. 이미 공동구매인 상품은 구분을 유지한다. */
    if (value === 'SALE') return
    form.mallKind = 'REGULAR'
    form.orderStep = 1
    form.minOrder = 1
  },
})

const originKinds = ORIGIN_KIND_OPTIONS
const originMust = computed(() => originRequiredFor(form.category))
const originKind = computed<OriginKind>({
  get: () => form.originKind || '',
  set(value) {
    if (form.originKind !== value) form.originDetail = ''
    form.originKind = value
    form.origin = composeOrigin(form.originKind, form.originDetail)
  },
})
const originDetail = computed({
  get: () => form.originDetail,
  set(value: string) {
    form.originDetail = value
    form.origin = composeOrigin(form.originKind, form.originDetail)
  },
})
const originHint = computed(() =>
  originMust.value
    ? '식품·건강은 원산지를 골라야 검수를 요청할 수 있습니다. 수입산은 국가명, 혼합은 국가별 원산지를 적습니다.'
    : '이 분류는 원산지를 비워 둘 수 있습니다. 표시해야 하는 품목만 고르세요.',
)
const sellNoun = computed(() => sellUnitNoun(form.sellUnit))
const priceQty = computed(() => {
  const max = Math.trunc(Number(form.allocShipMax) || 0)
  if (max >= 1) return max
  return Math.trunc(Number(form.basisQty) || 0)
})
const shipQty = computed(() => Math.trunc(Number(form.allocShipMax) || 0))
/** 개수와 배송비 금액이 모두 있을 때만 공급가·판매가격을 적는다. 0원은 아직 안 적은 것이다. */
const shipLineReady = computed(() => shipQty.value >= 1 && Number(form.allocShipFee) > 0)
const goodsPriceLabel = computed(() => (shipQty.value >= 1 ? `${shipQty.value}개 ` : ''))
/** 공급가와 판매가격이 모두 있을 때만 구매 옵션을 고친다. */
const optionsReady = computed(
  () => shipLineReady.value && Number(form.basisCost) > 0 && Number(form.basisPrice) > 0,
)
const cartonPieces = computed(() => Math.max(1, Math.trunc(Number(form.packQty) || 1)))
const cartonPriceText = computed(() => money(Math.max(0, sellingPrice.value) * cartonPieces.value))
const sellingPrice = computed(() => Math.max(0, Number(form.price) || 0))
const priceText = computed(() => money(sellingPrice.value))
const customQty = ref<number | ''>('')

function money(value: number) {
  return `${Math.round(value).toLocaleString('ko-KR')}원`
}

function bundleTotal(opt: EditorOption) {
  return Math.max(0, sellingPrice.value + (Number(opt.extra) || 0))
}

/** 개당마진 = (개당판매가 - (개당공급가 + 배송비)) / 2 */
function optionBreakdown(qty: number, total: number) {
  const quoted = quoteOf(qty)
  if (!quoted) return null
  const unitSell = Math.round(total / qty)
  const unitSupply = Math.round(quoted.goodsCost / qty)
  const unitShip = quoted.unitShip
  const unitMargin = Math.round((unitSell - (unitSupply + unitShip)) / 2)
  return { unitSupply, unitShip, unitSell, unitMargin }
}

function bundleNote(qty: number | null, total: number) {
  if (qty === 1) return '1개 기준가와 같습니다'
  if (!qty) return '개수로 시작하는 이름만 묶음으로 계산합니다'
  return `개당 ${money(Math.round(total / qty))}`
}

const qtyRows = computed(() =>
  form.options
    .filter((opt) => opt.use !== false && opt.name.trim())
    .map((opt) => {
      const qty = optionPieceCount(opt.name)
      const total = bundleTotal(opt)
      return {
        opt,
        qty,
        total,
        note: bundleNote(qty, total),
        breakdown: qty ? optionBreakdown(qty, total) : null,
      }
    })
    .sort((a, b) => (a.qty ?? 9999) - (b.qty ?? 9999) || a.opt.name.localeCompare(b.opt.name, 'ko')),
)

function hasQty(count: number) {
  return form.options.some((opt) => opt.use !== false && optionPieceCount(opt.name) === count)
}

function setFront(opt: EditorOption) {
  if (!optionsReady.value) return
  for (const row of form.options) row.front = row.key === opt.key
}

function addQty(raw: number | '') {
  if (!optionsReady.value) return
  const count = Math.floor(Number(raw) || 0)
  if (count < 1) {
    emit('notify', '1개 이상부터 넣을 수 있습니다.')
    return
  }
  if (count > 999) return
  if (hasQty(count)) return
  const unit = sellingPrice.value
  const quoted = quoteOf(count)
  const group =
    form.options.find((opt) => opt.use !== false && opt.name.trim())?.group.trim() || '구성'
  const alone = !form.options.some((opt) => opt.use !== false && opt.name.trim())
  let extra = count === 1 ? 0 : unit * (count - 1)
  if (quoted && count === 1 && !quoteHold) {
    form.price = quoted.recommended
    extra = 0
  } else if (quoted && count !== 1) {
    extra = quoted.recommended - unit
  }
  form.options.push({
    key: nextRowKey(),
    group,
    name: `${count}개`,
    extra,
    stock: 0,
    use: true,
    front: alone,
    priceLocked: false,
  })
  customQty.value = ''
}

const quoteInput = computed(() => ({
  basisQty: priceQty.value,
  basisCost: Math.round(Number(form.basisCost) || 0),
  basisPrice: Math.round(Number(form.basisPrice) || 0),
  shipFee: Math.round(Number(form.allocShipFee) || 0),
  shipMax: Math.trunc(Number(form.allocShipMax) || 0),
}))

const quoteReady = computed(() => packQuoteReady(quoteInput.value))

function quoteOf(qty: number | null): PackQuote | null {
  if (!qty || qty < 1) return null
  return quotePack(qty, quoteInput.value)
}

let quoteHold = false

function setOptionTotal(opt: EditorOption, total: number) {
  const qty = optionPieceCount(opt.name)
  if (qty === 1) {
    form.price = total
    opt.extra = 0
    return
  }
  opt.extra = total - sellingPrice.value
}

function applyQuoteToOptions() {
  quoteHold = true
  for (const opt of form.options) {
    if (opt.use === false) continue
    const qty = optionPieceCount(opt.name)
    if (!qty) continue
    const quoted = quoteOf(qty)
    if (!quoted) continue
    opt.priceLocked = false
    opt.extra = qty === 1 ? 0 : quoted.recommended - sellingPrice.value
  }
  nextTick(() => {
    quoteHold = false
  })
}

function fillSuggestedPrices() {
  if (!optionsReady.value) {
    emit('notify', '개당 배송비와 그 수량의 공급가, 판매가격을 먼저 입력해 주세요.')
    return
  }
  syncQuotePrices()
  const maxQty = quoteInput.value.shipMax || quoteInput.value.basisQty
  for (const count of suggestPackQtys(maxQty)) {
    if (!hasQty(count)) addQty(count)
  }
  applyQuoteToOptions()
}

function recalculateOptions() {
  if (!optionsReady.value) {
    emit('notify', '개당 배송비와 그 수량의 공급가, 판매가격을 먼저 입력해 주세요.')
    return
  }
  const live = form.options.some((opt) => opt.use !== false && optionPieceCount(opt.name))
  if (!live) {
    emit('notify', '계산할 옵션이 없습니다. 개수를 추가하세요.')
    return
  }
  syncQuotePrices()
  applyQuoteToOptions()
}

function onBundlePrice(opt: EditorOption, total: number) {
  if (!optionsReady.value) return
  opt.priceLocked = true
  setOptionTotal(opt, Math.max(0, Math.round(Number(total) || 0)))
}

const previewTab = ref<'card' | 'buy'>('card')

const previewProduct = computed(() => {
  const product = toPreviewProduct(form, {
    storeName: props.storeName,
    storeCutoff: props.storeShipping.cutoff,
    storeReturnFee: props.storeShipping.returnFee,
    storeReturnPolicy: props.storeShipping.returnPolicy,
  })
  product.soldOut = props.saleStatus === 'SOLDOUT' || (product.stock ?? 0) <= 0
  return product
})

function removeOption(opt: EditorOption) {
  if (!optionsReady.value) return
  const index = form.options.findIndex((row) => row.key === opt.key)
  if (index >= 0) form.options.splice(index, 1)
}

function removeImage(index: number) {
  form.images.splice(index, 1)
}

function pickImages() {
  if (uploading.value) return
  blockImageKey.value = null
  fileInput.value?.click()
}

function pickBlockImage(key: number) {
  if (uploading.value) return
  blockImageKey.value = key
  fileInput.value?.click()
}

function addDetailImages() {
  if (uploading.value) return
  blockImageKey.value = 'new'
  fileInput.value?.click()
}

function onDetailDrop(event: DragEvent) {
  detailDragOver.value = false
  const files = Array.from(event.dataTransfer?.files ?? []).filter((file) => file.type.startsWith('image/'))
  if (!files.length || uploading.value) return
  void uploadDetailImages(files)
}

function blockLabel(type: MallDetailBlock['type']) {
  return BLOCK_LABEL[type]
}

function addBlock(type: MallDetailBlock['type']) {
  form.blocks.push({ key: nextRowKey(), type, text: '', imageUrl: '', alt: '' })
}

function moveBlock(index: number, step: number) {
  const next = index + step
  if (next < 0 || next >= form.blocks.length) return
  const [moved] = form.blocks.splice(index, 1)
  form.blocks.splice(next, 0, moved)
}

function removeBlock(index: number) {
  form.blocks.splice(index, 1)
}

async function onFiles(event: Event) {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files || [])
  input.value = ''
  const target = blockImageKey.value
  blockImageKey.value = null
  if (!files.length) return
  if (target === 'new') {
    await uploadDetailImages(files)
    return
  }
  const room = target == null ? maxImages - form.images.length : 1
  if (room <= 0) {
    emit('notify', `이미지는 ${maxImages}장까지 올릴 수 있습니다.`)
    return
  }
  uploading.value = true
  try {
    const uploaded = await props.uploader(files.slice(0, room))
    if (typeof target === 'number') {
      const block = form.blocks.find((item) => item.key === target)
      if (block && uploaded[0]?.url) block.imageUrl = uploaded[0].url
      return
    }
    for (const image of uploaded) {
      if (image.url && form.images.length < maxImages) form.images.push(image.url)
    }
  } catch (error) {
    emit('notify', error instanceof Error ? error.message : '이미지를 올리지 못했습니다.')
  } finally {
    uploading.value = false
  }
}

async function uploadDetailImages(files: File[]) {
  if (!files.length || uploading.value) return
  uploading.value = true
  try {
    const uploaded = await props.uploader(files.slice(0, 20))
    let count = 0
    for (const image of uploaded) {
      if (!image.url) continue
      form.blocks.push({ key: nextRowKey(), type: 'IMAGE', text: '', imageUrl: image.url, alt: '' })
      count += 1
    }
    if (count) emit('notify', `본문 이미지 ${count}장을 올렸습니다.`)
  } catch (error) {
    emit('notify', error instanceof Error ? error.message : '이미지를 올리지 못했습니다.')
  } finally {
    uploading.value = false
  }
}

function lockFreeShipping() {
  if (form.shippingFee !== 0) form.shippingFee = 0
  if (form.shippingCharge !== 'ONCE') form.shippingCharge = 'ONCE'
}

let syncingQuote = false

function syncQuotePrices() {
  const max = Math.trunc(Number(form.allocShipMax) || 0)
  if (max >= 1 && Math.trunc(Number(form.basisQty) || 0) !== max) form.basisQty = max
  if (mallKind.value === 'SALE' || syncingQuote || !quoteReady.value) return
  const unit = quoteOf(1)
  if (!unit) return
  syncingQuote = true
  quoteHold = true
  const prev = Math.max(0, Number(form.price) || 0)
  form.cost = unit.goodsCost
  form.price = unit.recommended
  for (const opt of form.options) {
    if (opt.use === false) continue
    const qty = optionPieceCount(opt.name)
    if (!qty) continue
    if (qty === 1) {
      opt.extra = 0
      continue
    }
    if (opt.priceLocked) {
      opt.extra = Math.round(prev + (Number(opt.extra) || 0)) - unit.recommended
    } else {
      const quoted = quoteOf(qty)
      if (quoted) opt.extra = quoted.recommended - unit.recommended
    }
  }
  syncingQuote = false
  nextTick(() => {
    quoteHold = false
  })
}

watch(
  () => [form.allocShipMax, form.allocShipFee, form.basisCost, form.basisPrice] as const,
  () => {
    syncQuotePrices()
  },
)

watch(() => form.shippingFee, lockFreeShipping)
watch(() => form.shippingCharge, lockFreeShipping)

watch(
  () => form.packQty,
  () => {
    if (mallKind.value !== 'SALE') return
    applySaleCarton(form)
  },
)

watch(sellingPrice, (next, prev) => {
  if (quoteHold || prev == null || next === prev) return
  for (const opt of form.options) {
    const qty = optionPieceCount(opt.name)
    if (qty === 1) {
      opt.extra = 0
      continue
    }
    const extra = Number(opt.extra) || 0
    if (prev <= 0 && extra === 0 && qty && qty > 1) {
      opt.extra = next * (qty - 1)
      continue
    }
    opt.extra = Math.round(prev + extra) - next
  }
})

watch(
  () => `${mallKind.value}|${form.options.map((opt) => `${opt.name}:${opt.use}`).join(',')}`,
  () => {
    if (mallKind.value !== 'REGULAR') return
    const live = form.options.filter((opt) => opt.use !== false && opt.name.trim())
    const pieces = live.filter((opt) => optionPieceCount(opt.name) != null)
    if (pieces.length && pieces.length === live.length && !pieces.some((opt) => opt.front)) {
      pieces[0].front = true
    }
    if (!pieces.length || pieces.length !== live.length) return
    for (const opt of pieces) {
      if (opt.stock) opt.stock = 0
    }
  },
  { immediate: true },
)

onMounted(() => {
  lockFreeShipping()
  form.marginShare = DEFAULT_MARGIN_SHARE
  if (form.mallKind !== 'REGULAR' && form.mallKind !== 'SALE') {
    form.mallKind = 'REGULAR'
  }
})
</script>

<style lang="scss" src="./ProductRegisterSheet.scss"></style>
